import { useState, useMemo } from 'react';
import { subDays, subMonths, startOfDay } from 'date-fns';
import type { DateFilterPreset, IDateRange } from '~/types';

function presetToRange(preset: DateFilterPreset): IDateRange {
  const now = new Date();
  const start = (() => {
    switch (preset) {
      case 'all':
        return null;
      case 'today':
        return startOfDay(now);
      case '7d':
        return subDays(now, 7);
      case '14d':
        return subDays(now, 14);
      case '1m':
        return subMonths(now, 1);
      case '3m':
        return subMonths(now, 3);
      case '6m':
        return subMonths(now, 6);
      case '12m':
        return subMonths(now, 12);
      case 'custom':
        return null;
    }
  })();

  return { preset, startDate: start, endDate: preset === 'all' ? null : now };
}

export function useDateFilter(defaultPreset: DateFilterPreset = 'all') {
  const [preset, setPreset] = useState<DateFilterPreset>(defaultPreset);
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);

  const dateRange = useMemo<IDateRange>(() => {
    if (preset === 'custom') {
      return { preset, startDate: customStart, endDate: customEnd };
    }
    return presetToRange(preset);
  }, [preset, customStart, customEnd]);

  return {
    preset,
    setPreset,
    dateRange,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
  };
}
